import api from './api';
import type {
  BackupLog,
  JobFileResult,
  JobFileResultStatus,
} from '../types/backup.types';
import type { ApiResponse, PaginationInfo } from '../types/api.types';

// Query params for GET /api/v1/backup-jobs/:id/files (backend ListJobFilesQuery: limit default 50, max 200)
export interface JobFilesParams {
  status?: JobFileResultStatus;
  page?: number;
  limit?: number;
}

export interface JobFilesPage {
  items: JobFileResult[];
  pagination: PaginationInfo;
}

// Query params for GET /api/v1/backup-jobs (backend ListJobsQuery: page default 1, limit default 25)
export interface BackupLogsParams {
  user_id?: string;
  page?: number;
  limit?: number;
}

// Backend list handler envelope: { items, pagination } (PaginationInfo mirrors Go)
export interface BackupLogsPage {
  items: BackupLog[];
  pagination: PaginationInfo;
}

export const backupService = {
  /**
   * Cancel a running backup job (backend POST /backup-jobs/:id/cancel).
   * Only jobs in `running` are cancellable; anything else answers 400
   * "Job is not running". Admin / sub_admin only (A-06, plan P-2).
   */
  async cancelJob(id: string): Promise<void> {
    await api.post(`/api/v1/backup-jobs/${id}/cancel`);
  },

  // 獲取備份記錄 (server-side pagination; backend default limit=25)
  async getLogs(params?: BackupLogsParams): Promise<BackupLogsPage> {
    const response = await api.get<ApiResponse<BackupLogsPage>>('/api/v1/backup-jobs', { params });
    const data = response.data.data;
    return {
      items: data?.items || [],
      pagination: data?.pagination || {
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        total: 0,
        total_pages: 0,
      },
    };
  },

  /**
   * 每檔結果（ADR-045）：GET /backup-jobs/:id/files → { items, pagination }。
   * 資料來自 agent 回報的 job_file_results；RBAC 與 job 本身相同。
   */
  async getJobFiles(jobId: string, params?: JobFilesParams): Promise<JobFilesPage> {
    const fallback = {
      page: params?.page ?? 1,
      limit: params?.limit ?? 50,
      total: 0,
      total_pages: 0,
    };
    const response = await api.get<ApiResponse<JobFilesPage>>(`/api/v1/backup-jobs/${jobId}/files`, {
      params,
    });
    const data = response.data.data;
    return {
      items: data?.items ?? [],
      pagination: data?.pagination ?? fallback,
    };
  },

  // 還原歷史 (GET /backup/restore-history) 移至 restore.service.ts (ADR-044)。

  // 清除所有失敗的備份記錄
  async clearFailedJobs(): Promise<{ count: number }> {
    const response = await api.delete<ApiResponse<{ count: number; message: string }>>('/api/v1/backup-jobs/failed');
    return { count: response.data.data?.count || 0 };
  },
};
